import {
  IUser,
  OnLogoutRequestCallbackType,
  OnAnalyticsRequestCallbackType,
  Nav
} from "@dietdoctor/elements";
import "@dietdoctor/elements/build/style.css";
import * as React from "react";
import { useLayoutEffect, useState } from "react";
import { getLang } from "./lang";
import { getUser } from "./user";
import { createRoot, hydrateRoot } from "react-dom/client";

type NavProps = React.ComponentProps<typeof Nav>;

/**
 * Applies the user WordPress already rendered into the page, as early as the mount path allows.
 *
 * This replaces a hard-coded `setTimeout(..., 250)`, which guaranteed a quarter-second of
 * anonymous nav on every page load even though `window.ckdc.user` was already in memory.
 *
 * `deferToHydration` is the whole subtlety, and a browser run is what settled it:
 *
 * - Hydrating (WordPress echoed the markup it fetched): that copy is shared and cached, so it is
 *   anonymous for everyone. The first render has to match it or hydration fails, so the user is
 *   applied in a layout effect straight afterwards.
 * - No markup (the fetch failed, or an empty container): there is nothing to match, so deferring
 *   buys nothing and costs a real anonymous frame — measured at roughly one frame before the
 *   member appeared. Render the user immediately instead.
 */
function NavWithLocalUser({ user, deferToHydration, ...props }: NavProps & { deferToHydration: boolean }) {
  const [navUser, setNavUser] = useState<IUser | undefined>(deferToHydration ? undefined : user);

  useLayoutEffect(() => {
    setNavUser(user);
  }, [user]);

  return <Nav user={navUser} {...props} />;
}

export function bootNav(win: Window, doc: Document) {
  const rootElement = doc.getElementById("header-new")!;

  const lang = getLang(doc);
  const user = getUser(win);

  const logoutCallback: OnLogoutRequestCallbackType = () => {
    return new Promise<void>(resolve => {
      //@ts-expect-error elements should be in the page
      elements.nav.setUser(undefined);
      win.ckdc?.logout().then(() => {
        resolve();
        win.location.reload();
      });
    });
  };

  const analyticsCallback: OnAnalyticsRequestCallbackType = (label: string) => {
    // GA4 event
    console.log("g4", "navbar", "Click", { label });
    win.ckdc?.ga4Event?.("navbar", "Click", { label });
  };

  const hasAdminBar = (d: Document) => d.getElementsByClassName("admin-bar").length > 0;

  const isHydrating = rootElement.hasChildNodes();

  const el = (
    <NavWithLocalUser
      hasAdminBar={hasAdminBar(doc)}
      lang={lang}
      user={user ?? undefined}
      deferToHydration={isHydrating}
      onLogoutRequestCallback={logoutCallback}
      onAnalyticsRequestCallback={analyticsCallback}
    />
  );

  if (isHydrating) {
    hydrateRoot(rootElement, el);
  } else {
    createRoot(rootElement).render(el);
  }
}
